import React, { useState } from "react";
import DefaultSearchSelectInput from "../../inputs/defaultSearchSelectInput";
import DefaultButton from "../../buttons/defaultButton";
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";

const TARGET_FIELDS = [
  { key: "first_name", label: "First Name", required: true, synonyms: ["firstname", "fname", "first name"] },
  { key: "last_name", label: "Last Name", required: true, synonyms: ["lastname", "lname", "last name", "surname"] },
  { key: "email", label: "Email", required: true, synonyms: ["email_address", "e-mail", "mail"] },
  { key: "phone", label: "Phone", required: false, synonyms: ["phone_number", "mobile", "contact", "telephone"] },
  { key: "employee_id", label: "Employee ID", required: false, synonyms: ["emp_id", "id", "employee number"] },
  { key: "department", label: "Department", required: false, synonyms: ["dept", "department_name"] },
  { key: "position", label: "Position", required: false, synonyms: ["job_title", "title", "role"] },
  { key: "salary", label: "Salary", required: false, synonyms: ["pay", "compensation", "wage"] },
  { key: "start_date", label: "Start Date", required: false, synonyms: ["hire_date", "joining_date", "date_of_joining"] },
];

const ColumnMapping = ({ detectedColumns, mapping, onMappingChange, onComplete }) => {
  const [localMapping, setLocalMapping] = useState(mapping);

  const handleMappingChange = (sourceColumn, targetField) => {
    const updated = { ...localMapping, [sourceColumn]: targetField?.value || null };
    setLocalMapping(updated);
    onMappingChange(updated);
  };

  const getSuggestedField = (sourceColumn) => {
    const colName = sourceColumn.toLowerCase().replace(/[_\s]/g, "");
    return TARGET_FIELDS.find(
      (field) =>
        field.key === colName ||
        field.synonyms.some((syn) => syn.toLowerCase() === colName)
    );
  };

  const getUnmappedColumns = () => {
    return detectedColumns.filter((col) => !localMapping[col.name]);
  };

  const getMappedTargetFields = () => {
    return Object.values(localMapping).filter(Boolean);
  };

  const getTargetFieldOptions = (sourceColumn) => {
    const alreadyMapped = getMappedTargetFields();
    return TARGET_FIELDS.map((field) => ({
      value: field.key,
      label: `${field.label}${field.required ? " *" : ""}`,
      isDisabled: alreadyMapped.includes(field.key) && localMapping[sourceColumn] !== field.key,
    }));
  };

  const requiredFieldsMapped = () => {
    const requiredFields = TARGET_FIELDS.filter((f) => f.required).map((f) => f.key);
    const mappedFields = Object.values(localMapping).filter(Boolean);
    return requiredFields.every((req) => mappedFields.includes(req));
  };

  const handleComplete = () => {
    if (!requiredFieldsMapped()) {
      alert("Please map all required fields before proceeding.");
      return;
    }
    onComplete();
  };

  return (
    <div className="flex flex-col gap-6" role="region" aria-label="Column mapping">
      <div>
        <h3 className="text-lg font-semibold text-theme mb-2">Map Columns</h3>
        <p className="text-sm text-textLight">
          Map your CSV columns to the required employee fields. Required fields are marked with *.
        </p>
      </div>

      {/* Detected Columns Info */}
      <div className="bg-information-50 border border-information-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HiExclamationCircle className="text-information-600 text-xl flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-information-900 mb-1">
              Detected {detectedColumns.length} columns in your file
            </p>
            <p className="text-xs text-information-700">
              Sample values shown to help identify columns. Auto-mapping suggestions are highlighted.
            </p>
          </div>
        </div>
      </div>

      {/* Mapping Table */}
      <div className="bg-white border border-borderColor rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" role="table" aria-label="Column mapping table">
            <thead className="bg-primary-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Source Column
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Sample Value
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Map To
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderColor">
              {detectedColumns.map((col, index) => {
                const suggested = getSuggestedField(col.name);
                const isMapped = !!localMapping[col.name];
                const mappedField = TARGET_FIELDS.find(
                  (f) => f.key === localMapping[col.name]
                );
                const isRequired = mappedField?.required;

                return (
                  <tr
                    key={col.name}
                    className={`
                      ${index % 2 === 0 ? "bg-white" : "bg-primary-50"}
                      ${suggested && !isMapped ? "bg-information-50" : ""}
                    `}
                  >
                    <td className="px-4 py-3 text-sm text-theme font-medium">
                      {col.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-textLight">
                      <span className="truncate max-w-xs block">{col.sample}</span>
                    </td>
                    <td className="px-4 py-3">
                      <DefaultSearchSelectInput
                        value={
                          localMapping[col.name]
                            ? getTargetFieldOptions(col.name).find(
                                (o) => o.value === localMapping[col.name]
                              )
                            : null
                        }
                        onChange={(selected) =>
                          handleMappingChange(col.name, selected)
                        }
                        options={getTargetFieldOptions(col.name)}
                        placeholder="Select field..."
                        customWidth="w-64"
                        lebelVertical={false}
                        info={
                          suggested
                            ? `Suggested: ${suggested.label}`
                            : undefined
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      {isMapped ? (
                        <div className="flex items-center gap-2">
                          <HiCheckCircle className="text-success-600" />
                          <span className="text-xs text-success-700">
                            {isRequired ? "Required" : "Mapped"}
                          </span>
                        </div>
                      ) : suggested ? (
                        <div className="flex items-center gap-2">
                          <HiExclamationCircle className="text-warning-600" />
                          <span className="text-xs text-warning-700">
                            Suggested
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-textLight">Not mapped</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unmapped Columns Warning */}
      {getUnmappedColumns().length > 0 && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <HiExclamationCircle className="text-warning-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-warning-900 mb-1">
                {getUnmappedColumns().length} column(s) not mapped
              </p>
              <p className="text-xs text-warning-700">
                Unmapped columns will be ignored during import.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Required Fields Check */}
      {!requiredFieldsMapped() && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <HiExclamationCircle className="text-error-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-error-900 mb-1">
                Required fields not mapped
              </p>
              <p className="text-xs text-error-700">
                Please map all required fields (marked with *) before proceeding.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <DefaultButton
          variant="default"
          name="Continue to Preview"
          onClick={handleComplete}
          disabled={!requiredFieldsMapped()}
        />
      </div>
    </div>
  );
};

export default ColumnMapping;

